import { createContext, useContext, useState, useEffect } from "react";
import { AuthService } from "../services/authService";
import { resetLogoutFlag } from "../services/api";
import { User } from "../types/user";
import { loginSchema } from "../validators/schemas";

interface AuthContextType {
  user: User | null;
  login: (user: string, pass: string) => Promise<boolean>;
  logout: () => Promise<void>;
  isLoading: boolean;
  error: any;
  isAuthenticated: boolean;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  // Verificar autenticación al cargar la aplicación
  useEffect(() => {
    const verifyAuth = async () => {
      try {
        // Primero intentar restaurar del sessionStorage
        const stored = sessionStorage.getItem("usuario");
        if (stored) {
          try {
            setUser(JSON.parse(stored));
            setIsLoading(false);
            return; // NO VERIFICAR CON EL SERVIDOR SI ESTÁ EN STORAGE
          } catch {
            sessionStorage.removeItem("usuario");
          }
        }

        // Solo verificar con el servidor si no estamos en login
        if (window.location.pathname === "/login") {
          setIsLoading(false);
          return;
        }

        // Luego verificar con el servidor si la cookie httpOnly es válida
        const response = await AuthService.verify();

        const usuario: User = {
          ...response.data,
          role: response.data.idRol === 1 ? "admin" : "operador",
        };

        sessionStorage.setItem("usuario", JSON.stringify(usuario));
        setUser(usuario);
      } catch {
        // Si falla la verificación, limpiar sesión completamente
        sessionStorage.removeItem("usuario");
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    verifyAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await AuthService.verify();
      
      const usuario: User = {
        ...response.data,
        role: response.data.idRol === 1 ? "admin" : "operador",
      };

      sessionStorage.setItem("usuario", JSON.stringify(usuario));
      setUser(usuario);
    } catch {
      sessionStorage.removeItem("usuario");
      setUser(null);
      throw new Error('No autenticado');
    }
  };

  const login = async (username: string, pass: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Validar entrada con Zod
      const validatedData = loginSchema.parse({ usuario: username, pass });

      const res = await AuthService.login(validatedData);

      const usuario: User = {
        ...res.data.usuario,
        role: res.data.usuario.idRol === 1 ? "admin" : "operador",
      };

      sessionStorage.setItem("usuario", JSON.stringify(usuario));
      setUser(usuario);
      return true;
    } catch (err: any) {
      const message =
        err.errors?.[0]?.message || err.response?.data?.message || "Error de conexión";
      setError({ message });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Limpiar datos ANTES de hacer logout
      sessionStorage.clear();
      setUser(null);
      resetLogoutFlag();

      // Llamar al backend para limpiar la cookie
      try {
        await AuthService.logout();
      } catch {
        // Continuar incluso si falla el logout en el servidor
      }
    } finally {
      // Asegurar que se limpia
      sessionStorage.clear();
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isLoading,
        error,
        isAuthenticated: !!user,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de AuthProvider");
  }
  return context;
};